import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link2, RefreshCcw, Save, ShieldCheck, Unplug } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { metaApi, type MetaAssetSelectionPayload } from '@/services/meta'
import { useAuthStore } from '@/stores/auth-store'

type AssetForm = {
  meta_business_id: string
  whatsapp_business_account_id: string
  phone_number_id: string
  catalog_id: string
  default_order_type_key: string
  auto_place_orders: string
}

const defaultForm: AssetForm = {
  meta_business_id: '',
  whatsapp_business_account_id: '',
  phone_number_id: '',
  catalog_id: '',
  default_order_type_key: 'PICKUP',
  auto_place_orders: 'true',
}

const defaultCatalogName = (serviceInstanceId: string | number | undefined) =>
  `Ellan Catalog ${serviceInstanceId ? `- ${serviceInstanceId}` : ''}`.trim()

const compactCount = (value: unknown) => Number(value ?? 0).toLocaleString()

export default function MetaCredentialsPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const authServiceInstanceId = useAuthStore((state) => state.serviceInstanceId)
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || authServiceInstanceId
  const [form, setForm] = useState<AssetForm>(defaultForm)
  const [catalogCreateName, setCatalogCreateName] = useState<string>('')

  const redirectUri = useMemo(() => {
    if (typeof window === 'undefined') {
      return ''
    }
    const url = new URL('/organization/settings/credentials/meta', window.location.origin)
    if (serviceInstanceId) {
      url.searchParams.set('service_instance_id', String(serviceInstanceId))
    }
    return url.toString()
  }, [serviceInstanceId])

  const connectionQuery = useQuery({
    queryKey: ['meta-connection', serviceInstanceId],
    queryFn: () => metaApi.getConnection(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  const syncRunsQuery = useQuery({
    queryKey: ['meta-sync-runs', serviceInstanceId],
    queryFn: () => metaApi.listSyncRuns(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  const webhookEventsQuery = useQuery({
    queryKey: ['meta-webhook-events', serviceInstanceId],
    queryFn: () => metaApi.listWebhookEvents(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  const orderTypesQuery = useQuery({
    queryKey: ['meta-order-types', serviceInstanceId],
    queryFn: () => metaApi.listOrderTypes(serviceInstanceId!),
    enabled: Boolean(serviceInstanceId),
  })

  useEffect(() => {
    const connection = connectionQuery.data
    setForm({
      meta_business_id: String(connection?.meta_business_id ?? ''),
      whatsapp_business_account_id: String(connection?.whatsapp_business_account_id ?? ''),
      phone_number_id: String(connection?.phone_number_id ?? ''),
      catalog_id: String(connection?.catalog_id ?? ''),
      default_order_type_key: String(connection?.default_order_type_key ?? 'PICKUP'),
      auto_place_orders: connection?.auto_place_orders === false ? 'false' : 'true',
    })
  }, [connectionQuery.data])

  useEffect(() => {
    if (!serviceInstanceId) {
      setCatalogCreateName('')
      return
    }
    setCatalogCreateName(defaultCatalogName(serviceInstanceId))
  }, [serviceInstanceId])

  const completeOAuthMutation = useMutation({
    mutationFn: metaApi.completeOAuth,
    onSuccess: async () => {
      toast.success('Meta OAuth connected. Select WABA, phone, and catalog assets next.')
      await queryClient.invalidateQueries({ queryKey: ['meta-connection', serviceInstanceId] })
      const params = new URLSearchParams(location.search)
      params.delete('code')
      params.delete('state')
      navigate(`/organization/settings/credentials/meta?${params.toString()}`, { replace: true })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const code = params.get('code')
    const state = params.get('state')
    if (!code || !state || !redirectUri || completeOAuthMutation.isPending || completeOAuthMutation.isSuccess) {
      return
    }
    completeOAuthMutation.mutate({ code, state, redirectUri })
  }, [completeOAuthMutation, location.search, redirectUri])

  const startOAuthMutation = useMutation({
    mutationFn: () => metaApi.startOAuth(serviceInstanceId!, redirectUri),
    onSuccess: (response) => {
      window.location.assign(response.oauth_url)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const saveAssetsMutation = useMutation({
    mutationFn: () => {
      const payload: MetaAssetSelectionPayload = {
        meta_business_id: form.meta_business_id.trim(),
        whatsapp_business_account_id: form.whatsapp_business_account_id.trim(),
        phone_number_id: form.phone_number_id.trim(),
        catalog_id: form.catalog_id.trim(),
        default_order_type_key: form.default_order_type_key.trim() || 'PICKUP',
        auto_place_orders: form.auto_place_orders === 'true',
      }
      return metaApi.selectAssets(serviceInstanceId!, payload)
    },
    onSuccess: async () => {
      toast.success('Meta assets saved')
      await queryClient.invalidateQueries({ queryKey: ['meta-connection', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const disconnectMutation = useMutation({
    mutationFn: () => metaApi.disconnect(serviceInstanceId!),
    onSuccess: async () => {
      toast.success('Meta connection disconnected')
      await queryClient.invalidateQueries({ queryKey: ['meta-connection', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const previewMutation = useMutation({
    mutationFn: () => metaApi.previewCatalog(serviceInstanceId!),
    onSuccess: (preview) => {
      toast.success(`Preview ready: ${compactCount(preview.count)} items, ${compactCount(preview.errors?.length)} errors`)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const createCatalogMutation = useMutation({
    mutationFn: () =>
      metaApi.createCatalog(serviceInstanceId!, {
        name: catalogCreateName.trim(),
        auto_select: true,
      }),
    onSuccess: async (payload) => {
      const createdCatalogId = String((payload.catalog ?? {}).id ?? '').trim()
      if (createdCatalogId) {
        setForm((current) => ({ ...current, catalog_id: createdCatalogId }))
      }
      toast.success(createdCatalogId ? `Catalog created (${createdCatalogId})` : 'Catalog created')
      await queryClient.invalidateQueries({ queryKey: ['meta-connection', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const syncMutation = useMutation({
    mutationFn: () => metaApi.syncCatalog(serviceInstanceId!),
    onSuccess: async () => {
      toast.success('Meta catalog sync queued')
      await queryClient.invalidateQueries({ queryKey: ['meta-sync-runs', serviceInstanceId] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  if (!serviceInstanceId) {
    return (
      <Card className="rounded-[24px]">
        <CardHeader>
          <CardTitle>Meta Credentials</CardTitle>
          <CardDescription>Select an organization/service instance before connecting Meta.</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  if (connectionQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-44 rounded-[24px]" />
        <Skeleton className="h-80 rounded-[24px]" />
      </div>
    )
  }

  const connection = connectionQuery.data
  const hasMetaConnection = Boolean(connection)
  const isConnected = connection?.status === 'connected'
  const canCreateCatalog = Boolean(connection?.has_access_token && (form.meta_business_id.trim() || connection?.meta_business_id))
  const canSaveAssets = Boolean(connection && connection.has_access_token)
  const connectMetaLabel = connection?.has_access_token ? 'Reconnect Meta' : 'Connect Meta'
  const latestSync = syncRunsQuery.data?.[0]
  const latestFailedEvent = webhookEventsQuery.data?.find((event) => event.status === 'failed')

  return (
    <div className="space-y-6">
      <Card className="rounded-[24px] border-border/80">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Meta Credentials</CardTitle>
            <CardDescription>
              Connect Meta OAuth, select WhatsApp Business assets, and monitor catalog/order integration.
            </CardDescription>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => startOAuthMutation.mutate()} disabled={startOAuthMutation.isPending || !redirectUri}>
              <Link2 className="mr-2 h-4 w-4" />
              {connectMetaLabel}
            </Button>
            {connection ? (
              <Button variant="outline" onClick={() => disconnectMutation.mutate()} disabled={disconnectMutation.isPending}>
                <Unplug className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm md:grid-cols-4">
          <div className="rounded-2xl border border-border/70 p-4">
            <p className="text-muted-foreground">Status</p>
            <p className="mt-1 font-semibold capitalize text-foreground">{connection?.status?.split('_').join(' ') || 'not connected'}</p>
          </div>
          <div className="rounded-2xl border border-border/70 p-4">
            <p className="text-muted-foreground">Access Token</p>
            <p className="mt-1 font-semibold text-foreground">{connection?.has_access_token ? 'Stored encrypted' : 'Missing'}</p>
          </div>
          <div className="rounded-2xl border border-border/70 p-4">
            <p className="text-muted-foreground">Latest Sync</p>
            <p className="mt-1 font-semibold capitalize text-foreground">{latestSync?.status || 'none'}</p>
          </div>
          <div className="rounded-2xl border border-border/70 p-4">
            <p className="text-muted-foreground">Webhook Errors</p>
            <p className="mt-1 font-semibold text-foreground">{latestFailedEvent ? 'Action needed' : 'None recent'}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-[24px] border-border/80">
        <CardHeader>
          <CardTitle>Meta Assets</CardTitle>
          <CardDescription>After OAuth, select the Business, WABA, phone number, and catalog used by this organization.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!hasMetaConnection ? (
            <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
              <span>Connect Meta first to create a connection for this branch. After OAuth completes, you can save the WABA, phone number, and catalog assets here.</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-amber-300 bg-white/80 text-amber-950 hover:bg-white"
                onClick={() => startOAuthMutation.mutate()}
                disabled={startOAuthMutation.isPending || !redirectUri}
              >
                <Link2 className="mr-2 h-4 w-4" />
                {startOAuthMutation.isPending ? 'Connecting...' : connectMetaLabel}
              </Button>
            </div>
          ) : !connection?.has_access_token ? (
            <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between">
              <span>Reconnect Meta before saving assets for this branch.</span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-amber-300 bg-white/80 text-amber-950 hover:bg-white"
                onClick={() => startOAuthMutation.mutate()}
                disabled={startOAuthMutation.isPending || !redirectUri}
              >
                <Link2 className="mr-2 h-4 w-4" />
                {startOAuthMutation.isPending ? 'Connecting...' : 'Reconnect Meta'}
              </Button>
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-2">
            {[
              ['meta_business_id', 'Meta business ID'],
              ['whatsapp_business_account_id', 'WhatsApp Business Account ID'],
              ['phone_number_id', 'Phone number ID'],
              ['catalog_id', 'Catalog ID'],
            ].map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label htmlFor={key}>{label}</Label>
                <Input
                  id={key}
                  value={form[key as keyof AssetForm]}
                  disabled={!canSaveAssets}
                  onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label htmlFor="default_order_type_key">Default Ellan order type</Label>
              <Select
                value={form.default_order_type_key}
                onValueChange={(value) => setForm((current) => ({ ...current, default_order_type_key: value }))}
                disabled={!canSaveAssets || orderTypesQuery.isLoading}
              >
                <SelectTrigger id="default_order_type_key">
                  <SelectValue placeholder={orderTypesQuery.isLoading ? 'Loading order types...' : 'Select order type'} />
                </SelectTrigger>
                <SelectContent>
                  {(orderTypesQuery.data ?? []).map((orderType) => (
                    <SelectItem key={orderType.key} value={orderType.key}>
                      {orderType.label} ({orderType.key})
                    </SelectItem>
                  ))}
                  {form.default_order_type_key && !(orderTypesQuery.data ?? []).some((orderType) => orderType.key === form.default_order_type_key) ? (
                    <SelectItem value={form.default_order_type_key}>
                      {form.default_order_type_key}
                    </SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Meta stores the selected order type key/code for webhook-created orders.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="auto_place_orders">WhatsApp order handling</Label>
              <Select
                value={form.auto_place_orders}
                onValueChange={(value) => setForm((current) => ({ ...current, auto_place_orders: value }))}
                disabled={!canSaveAssets}
              >
                <SelectTrigger id="auto_place_orders">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Auto-place validated orders</SelectItem>
                  <SelectItem value="false">Receive only, do not auto-place</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {connection?.last_error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
              {connection.last_error}
            </div>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <div className="flex min-w-[320px] flex-1 items-end gap-2">
              <div className="w-full space-y-2">
                <Label htmlFor="catalog_create_name">Create catalog</Label>
                <Input
                  id="catalog_create_name"
                  value={catalogCreateName}
                  placeholder={defaultCatalogName(serviceInstanceId)}
                  onChange={(event) => setCatalogCreateName(event.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => createCatalogMutation.mutate()}
                disabled={!canCreateCatalog || !catalogCreateName.trim() || createCatalogMutation.isPending}
              >
                {createCatalogMutation.isPending ? 'Creating…' : 'Create Catalog'}
              </Button>
            </div>
            <Button variant="outline" onClick={() => previewMutation.mutate()} disabled={!isConnected || previewMutation.isPending}>
              <ShieldCheck className="mr-2 h-4 w-4" />
              Preview Catalog
            </Button>
            <Button variant="outline" onClick={() => syncMutation.mutate()} disabled={!isConnected || syncMutation.isPending}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              Sync Catalog
            </Button>
            <Button onClick={() => saveAssetsMutation.mutate()} disabled={!canSaveAssets || saveAssetsMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              Save Meta Assets
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="rounded-[24px] border-border/80">
          <CardHeader>
            <CardTitle>Recent Sync Runs</CardTitle>
            <CardDescription>Latest catalog pushes to Meta.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {(syncRunsQuery.data ?? []).slice(0, 5).map((run) => (
              <div key={run.id} className="rounded-2xl border border-border/70 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium capitalize">{run.status || 'unknown'}</span>
                  <span className="text-muted-foreground">{run.created_on ? new Date(run.created_on).toLocaleString() : ''}</span>
                </div>
                <p className="mt-1 text-muted-foreground">
                  Total {compactCount(run.total_count)}, success {compactCount(run.success_count)}, failed {compactCount(run.failed_count)}, skipped {compactCount(run.skipped_count)}
                </p>
              </div>
            ))}
            {!syncRunsQuery.data?.length ? <p className="text-muted-foreground">No sync runs yet.</p> : null}
          </CardContent>
        </Card>

        <Card className="rounded-[24px] border-border/80">
          <CardHeader>
            <CardTitle>Recent Webhook Events</CardTitle>
            <CardDescription>WhatsApp order ingestion status.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {(webhookEventsQuery.data ?? []).slice(0, 5).map((event) => (
              <div key={event.id} className="rounded-2xl border border-border/70 p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium capitalize">{event.status || 'unknown'}</span>
                  <span className="text-muted-foreground">{event.received_at ? new Date(event.received_at).toLocaleString() : ''}</span>
                </div>
                <p className="mt-1 text-muted-foreground">Message: {event.message_id || event.event_id || '-'}</p>
                {event.linked_order_id ? <p className="text-muted-foreground">Linked order: {event.linked_order_id}</p> : null}
                {event.error ? <p className="mt-1 text-destructive">{event.error}</p> : null}
              </div>
            ))}
            {!webhookEventsQuery.data?.length ? <p className="text-muted-foreground">No webhook events yet.</p> : null}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
